import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { clearContainer, setContainer } from 'ducks/container/slices';
import { clearFile, updateFile } from 'ducks/file/slices';
import { getJob } from 'ducks/job/operators';
import { clearJob } from 'ducks/job/slices';
import { getJobParameters } from 'ducks/job-parameters/operators';
import { jobs_page_query, jobs_post_data } from 'ducks/jobs/constants';
import { getJobLogs } from 'ducks/logs/operators';
import { clearLogs } from 'ducks/logs/slices';
import { clearParameters } from 'ducks/parameters/slices';
import { getProject } from 'ducks/project/operators';
import { getProjectParameters } from 'ducks/project-parameters/operators';
import { getProjects } from 'ducks/projects/operators';
import { updateSettings } from 'ducks/settings/slices';
import { Slices } from 'ducks/slices';
import { clearStandardOut, updateStandardOut } from 'ducks/standard-out/slices';
import {
  JobsContainersCurrentAPI,
  JobsFilesStdoutAPI,
  PostRequestMessage,
  ProjectsJobsListAPI,
} from 'ducks/types/api-types';
import { EditJobData, JobStandardOutData } from 'ducks/types/common-types';
import { JobPageQuery, Query } from 'ducks/types/query-types';
import { FileType } from 'ducks/types/store-types';
import { store } from 'store';
import { Andromeda } from 'utils/axios';
import { handleError } from 'utils/errors';
import { replaceSearchQuery } from 'utils/queries';

export const LAST_BYTES_AMOUNT = 20000;

export const getJobs = createAsyncThunk<ProjectsJobsListAPI[], { project_id: number; data: Query }>(
  `${Slices.JOBS}/get`,
  async ({ project_id, data }, { rejectWithValue }) => {
    try {
      const post_data = {
        ...jobs_post_data,
        ...data,
      };
      const route = `/projects/${project_id}/jobs/list${replaceSearchQuery(post_data)}`;
      const res: AxiosResponse['data'] = await Andromeda.get(route);
      return res.data;
    } catch (error) {
      handleError(error);
      return rejectWithValue(error as AxiosError);
    }
  },
);

export const getJobsPage = async (project_id: number, queries: JobPageQuery) => {
  const query = {
    ...jobs_page_query,
    ...queries,
  };
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Promise.all([
    store.dispatch(getProjects()),
    store.dispatch(getProject(project_id)),
    store.dispatch(getJobs({ project_id, data: query })),
    store.dispatch(getProjectParameters(project_id)),
  ]);
  store.dispatch(updateSettings({ loading: false }));
  return res;
};

export const getJobPage = async (project_id: number, job_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  const [jobs] = await Promise.all([
    store.dispatch(getJob(job_id)),
    store.dispatch(getJobLogs(job_id)),
    store.dispatch(getJobParameters(job_id)),
    store.dispatch(getProjects()),
    store.dispatch(getProject(project_id)),
    store.dispatch(getProjectParameters(project_id)),
  ]);
  store.dispatch(updateSettings({ loading: false }));
  return jobs;
};

export const clearJobPage = () => {
  store.dispatch(clearJob());
  store.dispatch(clearLogs());
  store.dispatch(clearContainer());
  store.dispatch(clearParameters());
  store.dispatch(clearFile());
  store.dispatch(clearStandardOut());
};

export const getJobFile = async (job_id: number): Promise<Partial<JobsFilesStdoutAPI>> => {
  const res: AxiosResponse['data'] = await Andromeda.get(`/jobs/${job_id}/files/stdout`, {});
  return res.data;
};

export const getJobPolling = async (job_id: number, file: FileType) => {
  await store.dispatch(getJobLogs(job_id));
  await store.dispatch(getJob(job_id));

  const { container_id } = await getJobContainer(job_id);
  const { size_bytes, file_id } = await getJobFile(job_id);

  if (!container_id || !size_bytes) {
    return;
  }

  const { ending_byte } = file;

  // DEV NOTE: this will trigger the first time a file has standard out
  if (ending_byte === 0 && size_bytes !== 0) {
    const starting_byte = size_bytes > LAST_BYTES_AMOUNT ? size_bytes - LAST_BYTES_AMOUNT : 0;

    await getJobStandardOut(container_id, { starting_byte, ending_byte: size_bytes });
    store.dispatch(updateFile({ file_id, ending_byte: size_bytes }));
    return;
  }

  // DEV NOTE: persist the position of the last ending byte and increment by 1
  if (size_bytes > ending_byte) {
    const byte_difference = size_bytes - ending_byte;
    const starting_byte = byte_difference > LAST_BYTES_AMOUNT ? size_bytes - LAST_BYTES_AMOUNT : ending_byte;

    await getJobStandardOut(container_id, { starting_byte, ending_byte: size_bytes });
    store.dispatch(updateFile({ file_id, ending_byte: size_bytes }));
  }
};

export const getJobContainer = async (job_id: number): Promise<Partial<JobsContainersCurrentAPI>> => {
  const res: AxiosResponse['data'] = await Andromeda.get(`/jobs/${job_id}/containers/current`, {});
  store.dispatch(setContainer(res.data));
  return res.data;
};

export const getJobStandardOut = async (container_id: number, data: JobStandardOutData) => {
  const post_data = {
    starting_byte: data.starting_byte,
    ending_byte: data.ending_byte,
  };

  const route = `/containers/${container_id}/stdout_contents_as_json${replaceSearchQuery(post_data)}`;
  const res: AxiosResponse['data'] = await Andromeda.get(route);

  store.dispatch(updateStandardOut(res.data.contents));
  return JSON.stringify(res.data.contents);
};

export const editJob = async (entity_id: number, data: EditJobData) => {
  const post_data = {
    batch_descriptor: data.batch_descriptor,
    timeout_seconds: data.timeout_seconds,
  };
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Andromeda.post(`/jobs/${entity_id}/update`, post_data);
  return res.data;
};

export const stopJob = async (entity_id: number, data: { message: string }): Promise<PostRequestMessage> => {
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Andromeda.post(`/jobs/${entity_id}/stop`, data);
  return res.data;
};
